import React, { useEffect, useState, useMemo } from 'react';
import FunctionsData from "../../../Functions/FunctionsData";
import GeneralTable from "../../../Components/GeneralTable";
import GeneralTableColumn from "../../../Components/GeneralTableColumn";
import LandingCommunityLogsTableRow from "./LandingCommunityLogsTableRow";
import { TextField, Backdrop, CircularProgress } from "@mui/material";
import { CSVLink } from "react-csv";
import moment from "moment";

export default function LandingCommunityLogsTableTeam({ account, targetPortal }) {

    // State Variables
    const [search, setSearch] = useState('');
    const [vouchers, setVouchers] = useState([]);
    const [vouchersData, setVouchersData] = useState([]);
    const [loadingHubAdIds, setLoadingHubAdIds] = useState(true);
    const [loadingVouchers, setLoadingVouchers] = useState(true);
    const [filteredData, setFilteredData] = useState([]);
    const [hubAdIdsString, setHubAdIdsString] = useState("");
    const [refreshTrigger, setRefreshTrigger] = useState(false);
    const groupIds = account.idTokenClaims.groups
        ? account.idTokenClaims.groups.join(",")
        : "";

    // Fetch the dropdown data and extract hubAdIds
    useEffect(() => {
        setLoadingHubAdIds(true);
        const targetPort = "both";

        FunctionsData.postDataReturn("/api/campaign/hubAdIds", {
            groups: groupIds,
            target: targetPort,
        })
            .then((response) => {
                if (Array.isArray(response)) {
                    const ids = response
                        .map((item) => item.id)
                        .filter((id) => id !== undefined && id !== null);
                    const commaSeparatedIds = ids.join(",");
                    setHubAdIdsString(commaSeparatedIds);
                } else {
                    console.error("Unexpected response format:", response);
                }
            })
            .catch((error) => {
                console.error("Error fetching dropdown data:", error);
            })
            .finally(() => setLoadingHubAdIds(false));
    }, [groupIds]);

    // Fetch vouchers when hubAdIdsString is updated
    useEffect(() => {
        if (hubAdIdsString) {
            setLoadingVouchers(true);
            FunctionsData.fetchDataGet(
                `/api/voucher/community/staffTeam/${hubAdIdsString}?targetPortal=${targetPortal}`,
                setVouchers,
                true,
                setLoadingVouchers
            );
        }
    }, [hubAdIdsString, refreshTrigger]);

    useEffect(() => {
        if (hubAdIdsString) {
            const portal = targetPortal.toLowerCase() === 'outreach'
      ? 'outreach'
      : 'community';
            setLoadingVouchers(true);
            FunctionsData.fetchDataGet(
                `/api/voucher/${portal}/vouchercount/${hubAdIdsString}/${groupIds}`,
                setVouchersData,
                true,
                setLoadingVouchers
            );
        }
    }, [hubAdIdsString]);

    // Filter vouchers based on search term
    useEffect(() => {
        const searchTerm = search.toLowerCase().trim();
        setFilteredData(
            (vouchers || []).filter((row) => {
                return (
                    !searchTerm ||
                    String(row.sn).toLowerCase().includes(searchTerm) ||
                    String(row.first).toLowerCase().includes(searchTerm) ||
                    String(row.last).toLowerCase().includes(searchTerm) ||
                    String(row.email).toLowerCase().includes(searchTerm) ||
                    String(row.phone).toLowerCase().includes(searchTerm) ||
                    String(row.staff_email).toLowerCase().includes(searchTerm) ||
                    String(row.a_name).toLowerCase().includes(searchTerm)
                );
            })
        );
    }, [search, vouchers, loadingVouchers]);

    // Compute Voucher Counts using useMemo
    const voucherCounts = useMemo(() => {
        let available = 0;
        let issued = 0;
        let redeemed = 0;
        let total_issued = 0;
        let total_redeemed = 0;
        if (vouchersData.length > 0) {
            // parse with radix
            issued         = parseInt(vouchersData[0].issued,        10);
            redeemed       = parseInt(vouchersData[0].redeemed,      10);
            available      = parseInt(vouchersData[0].total,         10);
            total_issued   = parseInt(vouchersData[0].total_issued,  10);
            total_redeemed = parseInt(vouchersData[0].total_redeemed,10);
          
            // replace any NaN with 0
            issued         = isNaN(issued)        ? 0 : issued;
            redeemed       = isNaN(redeemed)      ? 0 : redeemed;
            available      = isNaN(available)     ? 0 : available;
            total_issued   = isNaN(total_issued)  ? 0 : total_issued;
            total_redeemed = isNaN(total_redeemed)? 0 : total_redeemed;
          }
        return { available, issued, redeemed, total_issued, total_redeemed };
    }, [vouchersData]);

    // CSV Headers for export with separate columns for details & surgery details
    const csvHeaders = [
        { label: "Voucher", key: "sn" },
        { label: "Issued at", key: "issued_at" },
        { label: "Redeemed at", key: "redeemed_at" },
        { label: "Vet clinic", key: "vet_name" },

        // Details split into separate columns
        { label: "First Name", key: "first_name" },
        { label: "Last Name", key: "last_name" },
        { label: "Email", key: "email" },
        { label: "Phone", key: "phone" },
        { label: "Address", key: "address" },

        // Surgery details split into separate columns with separate Type and Sex
        { label: "Animal Type", key: "animal_type" },
        { label: "Sex", key: "animal_sex" },
        { label: "Animal Name", key: "animal_name" },
        { label: "Surgery Type", key: "surgery_type" },
        { label: "Surgery Date", key: "surgery_date" },
        { label: "Microchip Number", key: "microchip_number" },

        // Staff email column
        { label: "Staff Email", key: "staff_email" },
    ];

    // CSV Data mapped with details & surgery details split
    const csvData = filteredData.map(row => ({
        sn: row.sn,
        issued_at: row.created_at ? moment(row.created_at).format('DD/MM/YYYY') : '',
        redeemed_at: row.redeemed_at ? moment(row.redeemed_at).format('DD/MM/YYYY') : '',
        vet_name: row.vet_name || '',

        // Details separated
        first_name: row.first || '',
        last_name: row.last || '',
        email: row.email || '',
        phone: row.phone || '',
        address: [row.suburb, row.city, row.postcode].filter(Boolean).join(' '),

        // Surgery details separated - type and sex split
        animal_type: row.a_type || '',
        animal_sex: row.a_gender || '',
        animal_name: row.a_name || '',
        surgery_type: row.is_redeemed === 1 ? row.a_type_surgery || '' : '',
        surgery_date: (row.is_redeemed > 0 && row.a_date_surgery) ? moment(row.a_date_surgery).format('DD/MM/YYYY') : '',
        microchip_number: row.is_redeemed === 1 ? row.a_microchip_number || '' : '',

        // Staff email
        staff_email: row.staff_email || '',
    }));

    // Conditional Rendering for Loading States
    if (loadingHubAdIds || loadingVouchers) {
        return (
            <Backdrop open={true} style={{ zIndex: 1000 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <div style={{ padding: 20 }}>
            {/* Voucher Counts Section */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginRight: "20px",
                    marginBottom: "10px",
                }}
            >
                 <span style={{ marginRight: "15px" }}>
  Current Campaign : Available Vouchers: {voucherCounts?.available ?? 0}
</span>
<span style={{ marginRight: "15px" }}>
  Issued Vouchers: {voucherCounts?.issued ?? 0}
</span>
<span style={{ marginRight: "15px" }}>
  Redeemed Vouchers: {voucherCounts?.redeemed ?? 0}
</span>
            </div>
            <br />
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    marginRight: "20px",
                    marginBottom: "10px",
                }}
            >
                <span style={{ marginRight: "15px" }}>
                    Total Vouchers : Issued : {voucherCounts.total_issued}
                </span>
                <span style={{ marginRight: "15px" }}>
                    Redeemed : {voucherCounts.total_redeemed}
                </span>
            </div>

            {/* Export to CSV Button */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <CSVLink
                    data={csvData}
                    headers={csvHeaders}
                    filename="team_vouchers_export.csv"
                    className="MuiButtonBase-root MuiButton-root MuiButton-contained"
                    style={{
                        textDecoration: "none",
                        padding: "6px 16px",
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        borderRadius: "4px",
                        cursor: 'pointer',
                    }}
                >
                    DOWNLOAD VOUCHERS TO OPEN IN EXCEL
                </CSVLink>
            </div>

            {/* Search Field */}
            <TextField
                variant="standard"
                fullWidth
                placeholder="Search"
                sx={{ marginBottom: 2 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {/* Table Section */}
            <GeneralTable asDataTable settingsColumns="150px 1fr 1fr 1fr 1fr 1fr 1fr">
                <GeneralTableColumn header>Voucher</GeneralTableColumn>
                <GeneralTableColumn header>Issued at</GeneralTableColumn>
                <GeneralTableColumn header>Redeemed at</GeneralTableColumn>
                <GeneralTableColumn header>Vet clinic</GeneralTableColumn>
                <GeneralTableColumn header>Details</GeneralTableColumn>
                <GeneralTableColumn header>Surgery details</GeneralTableColumn>
                <GeneralTableColumn header>Staff email</GeneralTableColumn>
                {filteredData.map((row) => (
                    <LandingCommunityLogsTableRow
                        key={row.id}
                        row={row}
                        data={vouchers}
                        setLoading={setLoadingVouchers}
                        setRefreshTrigger={setRefreshTrigger}
                    />
                ))}
            </GeneralTable>
        </div>
    );
}
